import { rootApi } from '../rootApi';
import { setAuthDetails } from '../../features/auth/authSlice';

/**
 * Validates token data from response
 * @param {Object} tokenData - Token data from response
 * @param {string} operation - Operation name for error messages
 * @returns {boolean} True if valid
 */
const validateCookieAuthData = (authData, operation = 'authentication') => {
    if (!authData) {
        console.error(`Received empty response from ${operation} endpoint`);
        throw new Error(`${operation} failed: Empty response received`);
    }

    const { authenticated, csrfToken } = authData;

    if (!authenticated || !csrfToken) {
        console.error(`Missing auth data in ${operation} response`, {
            authenticated: !!authenticated,
            hasCSRFToken: !!csrfToken
        });
        throw new Error(`${operation} failed: Invalid authentication data received`);
    }

    return true;
};

/**
 * Transforms cookie-based auth response to standardized format
 * @param {Object} responseData - Response data
 * @param {Object} additionalData - Additional data to include
 * @returns {Object} Transformed response
 */
const transformCookieAuthResponse = (responseData, additionalData = {}) => {
    // Guard against null/undefined response
    if (!responseData || !responseData.data) {
        throw new Error('Authentication failed: Empty response received');
    }

    const { authenticated, csrfToken, tokenExpiration, message, roles, manageRoles, userId, userName } = responseData.data;
    // Validate critical fields
    validateCookieAuthData(responseData.data);

    // For cookie-based auth, we use dummy tokens since actual tokens are in HttpOnly cookies
    // The frontend will rely on cookies for authentication, CSRF token for security
    return {
        token: 'cookie-based-auth', // Dummy token to satisfy frontend state
        refreshToken: 'cookie-based-refresh', // Dummy refresh token
        csrfToken,
        authenticated,
        tokenExpiration,
        message,
        expiresIn: null, // Not applicable for cookie-based auth
        tokenType: 'Cookie',
        roles,
        manageRoles: manageRoles || [],
        userId,
        userName,
        ...additionalData
    };
};

// Separate API slice for authentication
export const authApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        // ============================================================================
        // AUTHENTICATION ENDPOINTS
        // ============================================================================
        getAuthDetails: builder.mutation({
            query: (credentials) => ({
                url: 'v1/auth/token',
                method: 'POST',
                body: {
                    email: credentials.email,
                    password: credentials.password,
                    isLdap: credentials.isLdap || false,
                    turnstileToken: credentials.turnstileToken ?? ''
                }
            }),
            transformResponse: (response, meta, arg) => {
                return transformCookieAuthResponse(response, {
                    user: {
                        email: arg.email
                    }
                });
            }
        }),

        refreshToken: builder.mutation({
            query: () => ({ 
                url: '/v1/auth/refresh',
                method: 'POST',
                body: {} // No body needed for cookie-based refresh
            }),
            transformResponse: (response) => {
                return transformCookieAuthResponse(response);
            }
        }),

        // Get auth details for global state
        login: builder.mutation({
            query: (credentials) => ({
                url: 'v1/auth/token',
                method: 'POST',
                body: {
                    email: credentials.email,
                    password: credentials.password,
                    isLdap: credentials.isLdap || false,
                    turnstileToken: credentials.turnstileToken ?? ''
                }
            }),
            transformResponse: (response, meta, arg) => {
                return transformCookieAuthResponse(response, {
                    user: {
                        email: arg.email
                    }
                });
            },
            async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
                try {
                    // Check if we already have auth details in store/storage
                    const currentAuth = getState().auth;
                    const hasValidData = currentAuth.userId && currentAuth.isAuthenticated;

                    // If we have valid cached data, don't fetch from API
                    if (hasValidData) {
                        console.log('Auth details already cached, skipping API call');
                        return;
                    }

                    const { data } = await queryFulfilled;
                    dispatch(setAuthDetails(data));
                } catch (error) {
                    console.error('Failed to fetch auth details:', error);
                }
            },
            providesTags: ['AuthDetails'],
            // Cache for 5 minutes to avoid unnecessary API calls
            keepUnusedDataFor: 300
        }),

        // ============================================================================
        // PASSWORD MANAGEMENT ENDPOINTS
        // ============================================================================
        forgotPassword: builder.mutation({
            query: (email) => ({
                url: '/v1/auth/forgot-password',
                method: 'POST',
                body: { email }
            })
        }),

        resetPassword: builder.mutation({
            query: (data) => ({
                url: '/v1/auth/reset-password',
                method: 'POST',
                body: data
            })
        }),

        // ============================================================================
        // TOKEN MANAGEMENT ENDPOINTS
        // ============================================================================
        revokeToken: builder.mutation({
            query: (refreshToken) => ({
                url: '/v1/auth/revoke',
                method: 'POST',
                body: { refreshToken }
            })
        }),

        validateToken: builder.mutation({
            query: (token) => ({
                url: '/v1/auth/validate',
                method: 'POST',
                body: { token }
            })
        }),

        // ============================================================================
        // SESSION MANAGEMENT ENDPOINTS
        // ============================================================================
        logout: builder.mutation({
            query: () => ({
                url: '/auth/logout',
                method: 'POST'
            })
        }),

        getUserProfile: builder.query({
            query: () => ({
                url: '/auth/profile',
                method: 'GET'
            })
        }),
        register: builder.mutation({
            query: (data) => ({
                url: '/v1/auth/register',
                method: 'POST',
                body: {
                    username: data.username,
                    email: data.email,
                    password: data.password,
                    firstName: data.firstName,
                    lastName: data.lastName
                }
            })
        })
    }),
    overrideExisting: false
});

// Export hooks for use in components
export const {
    useLoginMutation,
    useRefreshTokenMutation,
    useRevokeTokenMutation,
    useValidateTokenMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useLogoutMutation,
    useGetUserProfileQuery
    // useGetAuthDetailsQuery
} = authApi;
